import { cn } from "@/shared/lib/utils";
import { Quote } from "lucide-react";
import { useEffect, useState } from "react";
import { REFERENCES, type Reference } from "./references-data";

/** Kolik karet zobrazit najednou na desktopu. */
const CARDS_PER_PAGE = 3;
const ROTATION_MS = 10_000;

function chunk<T>(items: readonly T[], size: number): T[][] {
  const pages: T[][] = [];
  for (let i = 0; i < items.length; i += size) {
    pages.push(items.slice(i, i + size));
  }
  // Doplnit poslední stránku z prvních referencí, aby měla stejný počet.
  const last = pages[pages.length - 1];
  if (last && last.length < size) {
    let i = 0;
    while (last.length < size) {
      const next = items[i % items.length];
      if (next) last.push(next);
      i += 1;
    }
  }
  return pages;
}

/**
 * Auto-rotující slider referencí — 3 karty vedle sebe (na desktopu),
 * stránka se přepne každých 10 s. Respektuje prefers-reduced-motion.
 */
export function TestimonialSlider() {
  const pages = chunk(REFERENCES, CARDS_PER_PAGE);
  const [pageIdx, setPageIdx] = useState(0);

  useEffect(() => {
    const reduced = window.matchMedia("(prefers-reduced-motion: reduce)");
    if (reduced.matches || pages.length < 2) return;
    const interval = window.setInterval(() => {
      setPageIdx((i) => (i + 1) % pages.length);
    }, ROTATION_MS);
    return () => window.clearInterval(interval);
  }, [pages.length]);

  return (
    <section className="relative" data-testid="hca-home-testimonials" aria-label="Řekli o nás">
      <div className="mb-8 flex flex-wrap items-end justify-between gap-3">
        <div>
          <div className="flex items-center gap-3">
            <span aria-hidden="true" className="h-px w-8 bg-[color:var(--hca-gold)]" />
            <div className="font-semibold text-[color:var(--hca-gold)] text-sm uppercase tracking-[0.22em]">
              Řekli o nás
            </div>
          </div>
          <h3 className="mt-2 font-bold text-2xl tracking-tight sm:text-3xl">
            Skuteční klienti, skutečné výsledky
          </h3>
        </div>
        <div className="flex gap-1.5" aria-label="Navigace recenzí">
          {pages.map((_, i) => (
            <button
              // biome-ignore lint/suspicious/noArrayIndexKey: pages are stable order
              key={i}
              type="button"
              aria-label={`Strana ${i + 1} z ${pages.length}`}
              aria-pressed={i === pageIdx}
              onClick={() => setPageIdx(i)}
              className={cn(
                "h-2 w-8 rounded-full transition-colors",
                i === pageIdx
                  ? "bg-[color:var(--hca-gold)]"
                  : "bg-muted-foreground/30 hover:bg-muted-foreground/60",
              )}
              data-testid={`hca-home-testimonial-dot-${i}`}
            />
          ))}
        </div>
      </div>

      <div className="relative min-h-[280px]">
        {pages.map((page, i) => (
          <div
            // biome-ignore lint/suspicious/noArrayIndexKey: pages are stable order
            key={i}
            className={cn(
              "grid grid-cols-1 gap-5 transition-opacity duration-700 md:grid-cols-3",
              i === pageIdx
                ? "relative opacity-100"
                : "pointer-events-none absolute inset-0 opacity-0",
            )}
            aria-hidden={i === pageIdx ? "false" : "true"}
          >
            {page.map((r) => (
              <ReferenceCard key={`${i}-${r.slug}`} reference={r} />
            ))}
          </div>
        ))}
      </div>
    </section>
  );
}

function ReferenceCard({ reference: r }: { reference: Reference }) {
  return (
    <article
      className="relative flex h-full flex-col rounded-lg border border-[color:var(--hca-gold)]/30 bg-card p-5 shadow-sm transition hover:border-[color:var(--hca-gold)]/60 hover:shadow-md"
      data-testid={`hca-home-testimonial-${r.slug}`}
    >
      <Quote className="h-5 w-5 flex-shrink-0 text-[color:var(--hca-gold)]" aria-hidden="true" />
      <p className="mt-3 flex-1 text-foreground text-sm leading-relaxed">„{r.excerpt}"</p>

      {r.metric ? (
        <div className="mt-3 inline-flex w-fit rounded-md bg-[color:var(--hca-gold)] px-2.5 py-1 text-[color:var(--hca-navy)]">
          <span className="font-bold text-sm leading-tight">{r.metric.value}</span>
          <span className="ml-2 text-xs opacity-90">{r.metric.label}</span>
        </div>
      ) : null}

      <footer className="mt-4 flex items-center gap-3 border-border border-t pt-3">
        {r.photoSrc ? (
          <img
            src={r.photoSrc}
            alt=""
            aria-hidden="true"
            className="h-9 w-9 flex-shrink-0 rounded-full object-cover"
            loading="lazy"
          />
        ) : (
          <div aria-hidden="true" className="h-9 w-9 flex-shrink-0 rounded-full bg-muted" />
        )}
        <div className="min-w-0 flex-1">
          <div className="truncate font-semibold text-foreground text-xs">{r.author}</div>
          <div className="truncate text-[0.7rem] text-muted-foreground">{r.role}</div>
        </div>
        {r.logoSrc ? (
          <img
            src={r.logoSrc}
            alt=""
            aria-hidden="true"
            className={cn(
              "h-6 w-auto max-w-[60px] flex-shrink-0 object-contain",
              // Barevná/světlá loga necháme být; tmavé wordmarky (logoInvert) na
              // tmavé kartě obarvíme na bílou siluetu, aby nezanikly.
              r.logoInvert ? "opacity-80 brightness-0 invert" : "opacity-90",
            )}
            loading="lazy"
          />
        ) : null}
      </footer>
    </article>
  );
}
